import { useState, useRef, useCallback, useEffect, useLayoutEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { HardDrive, Settings, LayoutGrid, BookKey, Globe, X, SquareArrowOutUpRight, Pin, PinOff, Palette, Gauge, Pencil, FolderMinus, FolderOutput, FolderInput, ArrowUp, ArrowDown, Droplet, GripVertical, ChevronUp, ChevronDown, type LucideIcon } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { BrandLogo, useBrand } from '@papan/brand/react'
import { useTheme, getThemeClass } from './ThemeProvider.js'
import { useToastOptional } from './Toast.js'
import { useKeybookAvailable } from './keybookAvailability.js'
import { MiniappIcon } from './MiniappIcon.js'
import { NotificationBell, type NotificationBellProps } from './NotificationBell.js'
import { AvatarMenu } from './AvatarMenu.js'
import { useContextMenu } from './useContextMenu.js'
import { FileContextMenu, type FileMenuItem } from './FileContextMenu.js'
import { PDAppIcon } from './PDAppIcon'
import { FolderGlyph } from './rail/FolderGlyph.js'
import { FolderPopup, FOLDER_ROW_H } from './rail/FolderPopup.js'
import { FOLDER_COLOR_KEYS, FOLDER_COLOR_SWATCH, type FolderColorKey } from './rail/folderColors.js'
import { isMiniappFolder } from '@papan/types'
import { railApps, type RailApp, type RailEntry, type RailFolder } from './rail/composeRailEntries.js'
import { useRailDrag, type RailDragItem, type RailDropTarget } from './rail/useRailDrag.js'
import { railKeyOfPin, railKeyOfFolder, type FolderPatch } from './rail/dock.js'
import { RAIL_SHIFT_TRANSITION, prefersReducedMotion } from './rail/spring.js'
import { isKeyboardFocus } from './rail/focus.js'
import { isPapanDrag, readPapanDrag } from './papanDrag.js'
import { railFileDropEffect } from './rail/fileDropEffect.js'

// The well's geometry, shared by the layout (Tailwind classes below), the
// drag's slot arithmetic and the scroll fade — see rail/geometry.ts.
import { RAIL_TILE, RAIL_GAP, RAIL_SLOT, RAIL_PAD, RAIL_APP_GLYPH, RAIL_HTML_GLYPH, RAIL_FADE_PX, RAIL_FADE_MAX_FRACTION, RAIL_CHEVRON_MIN_ROWS, railFadeLength } from './rail/geometry.js'
import { followScroll, scrollStep, wheelDeltaPx, WHEEL_NOTCH_PX, type ScrollAim } from './rail/wheel.js'
/** No dragover for this long means an HTML5 drag has ended somewhere this window saw no event for (it left the window). */
const HTML_DRAG_QUIET_MS = 1000
/** How long a chevron that just went (its end reached) still takes a finger's tap, so tapping it again opens nothing. */
const CHEVRON_GRACE_MS = 700
const entryKey = (e: RailEntry) => (e.type === 'folder' ? railKeyOfFolder(e) : railKeyOfPin(e))

// A View is a surface the shell can OWN the URL for — not necessarily one with
// a rail button. `bin` and `quarantine` have lived here without one since they
// moved into the drive shell's side panel, and `launcher` is the same case from
// the other direction: the grid is routable and deck-hosted, but nothing points
// at it from the rail until a deployment opts in (plan Phase 6).
export type View = 'drive' | 'workspace' | 'chat' | 'keybook' | 'bin' | 'quarantine' | 'settings' | 'billing' | 'launcher' | 'apps'

/**
 * The surfaces an identity-only tenant does not have (§8, 0175).
 *
 * §8 lists "personal Drive, Bin, Quarantine, Agent, workspace and build entry
 * points" plus "Secure Storage/Keybook". `bin` and `quarantine` are not taskbar
 * items any more — they live inside the drive shell's side panel, which goes
 * with `drive` — but they are named here because this constant is the answer to
 * "which surfaces?" for every shell, and a reader should not have to know the
 * taskbar's current item list to trust it.
 *
 * Exported, and consumed by BOTH shells: `MobileShell` mounts its own tree, so
 * a rule written inline in the taskbar would be re-derived — and probably
 * differently — on mobile. `chat` is deliberately ABSENT: an identity-only user
 * can still be shown a chat surface inside a full organization they switch to,
 * and the landing override (`landingViewFor`) is what keeps them off the chat
 * home in a tenant that cannot back it.
 *
 * `settings` and `billing` are absent on purpose too. Account, authentication,
 * email, passkey and notification settings are explicitly KEPT by §8, and the
 * platform shelf is a cross-tenant admin surface that has nothing to do with
 * whether this tenant is a workspace. The sections WITHIN settings that go are
 * handled where they render, not here.
 */
export const IDENTITY_ONLY_HIDDEN_VIEWS: readonly View[] = [
  'drive', 'workspace', 'keybook', 'bin', 'quarantine',
  // NOT `apps` (#1484): the PDApps shell's Pinned and Recent follow the ACCOUNT, so an
  // identity-only tenant still shows them (AppsView `historyOnly` — no org part).
]

// The logo mark used to be a prism SVG hand-inlined here and hardcoded to
// `#AD46FF`. It is now `<BrandLogo />` from @papan/brand, which renders the
// brand's own compact-logo asset when one is published and otherwise the same
// prism filled from `--brand-primary`. The colour is still fixed PER BRAND
// (not per theme), so it keeps reading on both the dark and light taskbar
// rails — that property was the original reason for hardcoding it.

/** Active-tab indicator on the rail's right edge — a purple mark that
 *  ANIMATES between three states: a small dot (running in the background), a
 *  long bar (active), and gone (idle). Always mounted so the height can
 *  transition; `top-0 bottom-0 my-auto` centres it without a height-coupled
 *  transform, so it grows/shrinks smoothly from the middle. `border-radius:
 *  50%` keeps the dot a circle and gives the active bar softly-elliptical
 *  ends. */
/** An app (or a folder holding one) whose queued database writes failed and nobody has dismissed it — shown only to
 *  people who can edit the app (the count comes from a route that answers no one else). Leads to the app's settings. */
function WriteFailureDot() {
  return <span aria-hidden data-testid="rail-write-failure" className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 ring-1 ring-[var(--bg-primary)] pointer-events-none" />
}

/** `asleep` (#538): running but not loaded yet since the page loaded — the running dot, faint. */
function RailIndicator({ state }: { state: 'active' | 'running' | 'asleep' | 'idle' }) {
  return (
    <span
      aria-hidden
      className={`absolute top-0 bottom-0 my-auto rounded-[50%] bg-brand transition-all duration-300 ease-out ${
        state === 'active'
          ? '-right-0.5 w-1 h-4 opacity-100'
          : state === 'running'
            ? '-right-0.5 w-1 h-1 opacity-70'
            : state === 'asleep'
              // Smaller and faint, centred where the running dot sits.
              ? '-right-[1px] w-[2px] h-[2px] opacity-40'
              : '-right-0.5 w-1 h-0 opacity-0'
      }`}
    />
  )
}

/** One system-app entry on the rail. */
interface TaskBarItem {
  id: View
  /** A lucide glyph, or the PDApp mark (same `size`/`className` API). */
  icon: LucideIcon | typeof PDAppIcon
  labelKey: string
}


interface TenantInfo {
  id: string
  name: string
  type: string
  emails: Array<{ email: string; role: string }>
}

/** A user app (mini-app / standalone HTML) shown as a dock entry — either
 *  running, pinned (relaunchable), or both. The shape is composed in
 *  rail/composeRailEntries.ts; this alias keeps the old name for callers. */
export type TaskBarUserApp = RailApp

interface TaskBarProps {
  /** Null while a user app is active (its dock entry highlights instead). */
  activeView: View | null
  /** Apps running in the shell's keep-alive deck — shown with a dot (OS-dock style) */
  runningViews?: View[]
  /** The app well in dock order — apps and folders (rail/composeRailEntries). */
  railEntries?: RailEntry[]
  /** Folders (0195): drop one app on another (drag) — the callee pins what
   *  is not pinned, then creates; move an app into one (`folderId`) or back
   *  to the rail (`null`); rename; ungroup (the members return to the rail). */
  onCreateFolder?: (a: { kind: 'miniapp' | 'html'; ref: string }, b: { kind: 'miniapp' | 'html'; ref: string }, title?: string) => Promise<string | null> | void
  onMoveToFolder?: (kind: 'miniapp' | 'html', ref: string, folderId: string | null) => void
  /** Rearranged by drag: the whole top-level order as rail keys. */
  /** `movedKey` is the ONE entry the gesture moved. The order carries every
   *  top-level entry, INCLUDING running-but-unpinned apps that merely happen to
   *  be on the rail, so a consumer that treats the whole list as intent-to-pin
   *  would pin all of them. */
  onReorderRail?: (order: string[], movedKey: string) => void
  /** A popup row dropped into a gap: out of its folder AND into that slot. */
  onMoveToRail?: (kind: 'miniapp' | 'html', ref: string, order: string[]) => void
  /** Rearranged inside a folder: its member order as rail keys. */
  onReorderFolder?: (id: string, order: string[]) => void
  /** A file dragged from the drive or the home grid onto the well: pin it and
   *  place it in that slot (`order` is the rail with its key inserted). */
  /** Every app dropped in ONE gesture, plus the single resulting rail order.
   *  Batched deliberately: called per file, each with a whole-list order, N
   *  concurrent `PUT /rail` writes raced and the winner was arbitrary. */
  onPinToSlot?: (apps: Array<{ kind: 'miniapp' | 'html'; ref: string; title: string }>, order: string[]) => void
  onUpdateFolder?: (id: string, patch: FolderPatch) => void
  onDeleteFolder?: (id: string) => void
  onOpenUserApp?: (ref: string) => void
  /** Open a user app in a new browser tab (context menu). */
  onOpenUserAppNewTab?: (kind: 'miniapp' | 'html', ref: string) => void
  onCloseUserApp?: (kind: 'miniapp' | 'html', ref: string) => void
  /** Pin / unpin a user app (context menu) — keeps it on the dock when closed. */
  onPinUserApp?: (kind: 'miniapp' | 'html', ref: string, title?: string) => void
  onUnpinUserApp?: (kind: 'miniapp' | 'html', ref: string) => void
  /** Mini-apps only: open the app's icon editor (context menu). */
  onEditUserAppIcon?: (kind: 'miniapp' | 'html', ref: string) => void
  /** Close a running system app (drive/keybook/settings/billing/apps) — from the
   *  icon context menu. Chat home is the home base and is never closable. */
  onCloseSystemApp?: (view: 'drive' | 'keybook' | 'settings' | 'billing' | 'apps') => void
  /** Hide the Usage & Billing shelf item for anyone who is not a platform admin
   *  acting in the platform org (see `useBudgetBillingAccess`). Defaults to
   *  shown, which is why every mounting shell must pass it. */
  showBilling?: boolean
  /** This tenant is identity-only (0175) — drop the surfaces it does not have.
   *  See `IDENTITY_ONLY_HIDDEN_VIEWS`; hiding is disclosure, never enforcement. */
  identityOnly?: boolean
  /**
   * The surface the logo (the home button) opens, and the one it marks itself
   * active on. Comes from the deployment's `experience.landing` brand key.
   *
   * Defaults to `'chat'`, which is what every deployment did before the key
   * existed — so an omitted prop is the historical behaviour rather than a
   * blank home button. core-ui deliberately does not read the brand itself:
   * it has no feature imports and the mounting shell already resolves this
   * for its redirects, so passing it keeps ONE answer per shell.
   */
  homeView?: 'chat' | 'launcher'
  /** Open a system app in a new browser tab (context menu). */
  onOpenViewNewTab?: (view: View) => void
  onViewChange: (view: View) => void
  onWorkspaceDrop?: (files: { id: string; name: string; isFolder: boolean }[]) => void
  onDriveDrop?: (files: { id: string; name: string; isFolder: boolean }[]) => void
  onDriveUpload?: (files: FileList) => void
  workspacePopup?: ReactNode | ((closePopup: () => void) => ReactNode)
  /** Subscribe to live notification events (from useRealtime().onNotification) */
  onNotificationSubscribe?: NotificationBellProps['onSubscribe']
  /** The notification click handler (app layer opens the matching feature
   *  popup / navigates; actionability is decided by the registry + server). */
  onNotificationAction?: NotificationBellProps['onAction']
  /** Avatar menu props for org switcher. */
  user?: { name: string; email: string; image?: string }
  tenants?: TenantInfo[]
  activeTenantId?: string
  activeOrgEmail?: string
  onSwitchTenant?: (tenantId: string, orgEmail?: string) => void
  onCreateOrg?: () => void
  /** Passed straight through to `AvatarMenu` (§8.7). `undefined` = unresolved,
   *  which still renders the button; only a resolved `false` hides it. */
  canCreateOrg?: boolean
  /** The viewer's OWN personal tenant is identity-only — drops the Personal row
   *  from the avatar menu. A DIFFERENT question from `identityOnly` above, which
   *  is about the ACTIVE tenant and drives the rail filter; conflating the two
   *  offered a dead-end destination the moment such a person entered an org. */
  personalIsIdentityOnly?: boolean
  onSignOut?: () => void
  pendingInvites?: Array<{ inviteId: string; tenantId: string; tenantName: string; email: string; role: string }>
  onAcceptInvite?: (token: string) => void
  onDeclineInvite?: (token: string) => void
  onBlockInvite?: (token: string) => void
}

/** How long the pointer must rest on the Workspace icon before its popup opens (a mere pass-over doesn't). */
const WORKSPACE_POPUP_OPEN_DELAY_MS = 250

export function TaskBar({ activeView, runningViews, railEntries, onCreateFolder, onMoveToFolder, onReorderRail, onMoveToRail, onReorderFolder, onPinToSlot, onUpdateFolder, onDeleteFolder, onOpenUserApp, onOpenUserAppNewTab, onCloseUserApp, onPinUserApp, onUnpinUserApp, onEditUserAppIcon, onCloseSystemApp, showBilling, identityOnly, homeView = 'chat', onOpenViewNewTab, onViewChange, onWorkspaceDrop, onDriveDrop, onDriveUpload, workspacePopup, onNotificationSubscribe, onNotificationAction, user, tenants, activeTenantId, activeOrgEmail, onSwitchTenant, onCreateOrg, canCreateOrg, personalIsIdentityOnly, onSignOut, pendingInvites, onAcceptInvite, onDeclineInvite, onBlockInvite }: TaskBarProps) {
  const { t } = useTranslation('core-ui')
  const { productName } = useBrand()

  // ── Icon context menu (glass) — opens on right-click, press-and-hold
  // (mouse OR touch, via pointer events), or a second click on the
  // already-active icon; a click while it's open toggles it shut. Positioned
  // just right of the rail so it never covers the icon it belongs to.
  //
  // The owner icon owns its own toggle, so the hook's outside-mousedown
  // handler must NOT also close on a press of THAT icon — otherwise the
  // press's mousedown closes and the click re-opens (the flash). Each button
  // carries data-menu-owner=<key>; the hook ignores only the mousedown whose
  // key matches the open menu's owner (also neutralizing the touch-synthesized
  // mousedown that used to close a hold-opened menu). A press on any OTHER
  // target — a different icon, the bell, page content — still closes it, and
  // `owner` lets a press on the same icon detect "already open" and toggle. ──
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The drag hook is declared after the menus (it needs them); the hold timer
  // above reaches it through this ref.
  const dragRef = useRef<ReturnType<typeof useRailDrag> | null>(null)
  const holdFired = useRef(false)
  const pressStartRef = useRef<{ x: number; y: number } | null>(null)
  // Set when a pointerdown toggles an open menu shut, so the click that
  // follows the same press doesn't re-open it.
  const pressConsumedRef = useRef(false)
  // The owner key of the currently-open menu (kept in a ref so the hook's
  // ignoreTarget and the toggle check read it without a stale closure).
  const openOwnerRef = useRef<string | null>(null)

  // The menu stores a BUILDER, not items: a submenu's open/closed state lives
  // in React state and the rendered items must follow it, which a snapshot
  // taken at open time cannot.
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
  const iconMenu = useContextMenu<{ x: number; y: number; build: () => FileMenuItem[]; owner: string }>({
    // Only a press on the icon that OWNS the open menu is ignored — so
    // re-pressing it toggles, and the touch-synthesized mousedown doesn't
    // close it. Any OTHER target (a different icon, the bell, page content)
    // falls through and closes the menu.
    ignoreTarget: useCallback((target: EventTarget | null) => {
      const el = target as Element | null
      // A submenu's flyout is portaled OUTSIDE the menu element; a press on one
      // of its options must not read as "outside" or the option unmounts
      // before its click can fire.
      if (el?.closest?.('[data-file-context-submenu]')) return true
      const btn = el?.closest?.('[data-menu-owner]') as HTMLElement | null
      return !!btn && btn.dataset.menuOwner === openOwnerRef.current
    }, []),
  })
  openOwnerRef.current = iconMenu.menu?.owner ?? null

  const closeMenu = useCallback(() => { iconMenu.close(); setOpenSubmenu(null) }, [iconMenu])
  // The stored builder must reach the CURRENT menu functions: they close over
  // React state (which submenu is open), and a builder captured at open time
  // would keep reading the values from then.
  const buildersRef = useRef({ systemAppMenu: (_v: View): FileMenuItem[] => [], userAppMenu: (_a: TaskBarUserApp): FileMenuItem[] => [], folderMenu: (_f: RailFolder): FileMenuItem[] => [] })
  const openIconMenu = useCallback((owner: string, el: HTMLElement, build: () => FileMenuItem[]) => {
    if (build().length === 0) return
    const r = el.getBoundingClientRect()
    setOpenSubmenu(null)
    iconMenu.open({ x: Math.round(r.right + 8), y: Math.round(r.top), build, owner })
  }, [iconMenu])

  // Press-and-hold to open (works for mouse hold and touch hold alike). A
  // press on an icon whose menu is already open toggles it shut instead.
  const startHold = useCallback((owner: string, e: React.PointerEvent, build: () => FileMenuItem[]) => {
    // Only primary button (left / touch / pen); right-click is onContextMenu.
    if (e.button !== 0) return
    holdFired.current = false
    const el = e.currentTarget as HTMLElement
    // Toggle: pressing the icon that owns the open menu closes it.
    if (openOwnerRef.current === owner) {
      closeMenu()
      pressConsumedRef.current = true
      return
    }
    pressConsumedRef.current = false
    pressStartRef.current = { x: e.clientX, y: e.clientY }
    if (holdTimer.current) clearTimeout(holdTimer.current)
    holdTimer.current = setTimeout(() => { holdFired.current = true; dragRef.current?.cancelPending(); openIconMenu(owner, el, build) }, 450)
  }, [iconMenu, openIconMenu])
  const cancelHold = useCallback(() => {
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null }
  }, [])
  // Cancel the hold only past a movement threshold (a drag/scroll), not on
  // sub-pixel jitter — the per-pixel cancel was why hold never fired.
  const moveHold = useCallback((e: React.PointerEvent) => {
    if (!pressStartRef.current || !holdTimer.current) return
    const dx = e.clientX - pressStartRef.current.x
    const dy = e.clientY - pressStartRef.current.y
    if (dx * dx + dy * dy > 100) cancelHold()   // >10px → treat as drag/scroll
  }, [cancelHold])

  const systemAppMenu = useCallback((view: View): FileMenuItem[] => {
    const items: FileMenuItem[] = [
      { kind: 'item', icon: SquareArrowOutUpRight, label: t('taskBar.openNewTab'), onClick: () => { closeMenu(); onOpenViewNewTab?.(view) } },
    ]
    if ((view === 'drive' || view === 'keybook' || view === 'settings' || view === 'billing' || view === 'apps') && onCloseSystemApp && runningViews?.includes(view)) {
      items.push({ kind: 'separator' })
      items.push({ kind: 'item', icon: X, label: t('taskBar.close'), danger: true, onClick: () => { closeMenu(); onCloseSystemApp(view) } })
    }
    return items
  }, [t, closeMenu, onOpenViewNewTab, onCloseSystemApp, runningViews])

  // ── Announcements + the absorb pulse ──────────────────────────────────
  // A polite live region says what a drop or a menu move did — the drag has
  // no other voice for a screen reader — and a folder that just took an app
  // swells for a moment so the drop is acknowledged with its panel closed.
  const [announcement, setAnnouncement] = useState('')
  const announce = useCallback((text: string) => { setAnnouncement(''); requestAnimationFrame(() => setAnnouncement(text)) }, [])
  const [absorbKey, setAbsorbKey] = useState<string | null>(null)
  const absorbTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pulse = useCallback((key: string) => {
    setAbsorbKey(key)
    if (absorbTimer.current) clearTimeout(absorbTimer.current)
    absorbTimer.current = setTimeout(() => setAbsorbKey(null), 380)
  }, [])
  const labelOfKey = useCallback((key: string) => {
    for (const e of railEntries ?? []) {
      if (e.type === 'folder') {
        if (railKeyOfFolder(e) === key) return e.title || t('taskBar.folder')
        const m = e.members.find(x => railKeyOfPin(x) === key)
        if (m) return m.title || t('taskBar.app')
      } else if (railKeyOfPin(e) === key) return e.title || t('taskBar.app')
    }
    return t('taskBar.app')
  }, [railEntries, t])

  // ── Keyboard / touch path for what the drag does ──────────────────────
  // "Move up / down" and "Move to folder ▸" reach every arrangement the drag
  // can make, for anyone who cannot or would rather not drag. One rule for
  // both lists: swap with the neighbour, announce, persist.
  const moveEntryBy = useCallback((key: string, delta: -1 | 1) => {
    const list = railEntries ?? []
    const folder = list.find((e): e is RailFolder => e.type === 'folder' && e.members.some(m => railKeyOfPin(m) === key))
    const keys = folder ? folder.members.map(railKeyOfPin) : list.map(entryKey)
    const i = keys.indexOf(key)
    const j = i + delta
    if (i < 0 || j < 0 || j >= keys.length) return
    const next = [...keys]; next[i] = keys[j]!; next[j] = key
    if (folder) onReorderFolder?.(folder.id, next); else onReorderRail?.(next, key)
    announce(t('taskBar.a11y.moved', { item: labelOfKey(key), position: j + 1 }))
  }, [railEntries, onReorderFolder, onReorderRail, announce, t, labelOfKey])

  // `requestOpenFolder` is declared with the folder state, BELOW these menu
  // builders, and a `useCallback` deps array is evaluated during render — so a
  // builder cannot depend on it directly. Same indirection as `buildersRef`.
  const requestOpenFolderRef = useRef<(n: { id: string; renaming: boolean } | null) => void>(() => {})
  const userAppMenu = useCallback((app: TaskBarUserApp): FileMenuItem[] => {
    const key = railKeyOfPin(app)
    const list = railEntries ?? []
    const siblings = app.folderId
      ? (list.find((e): e is RailFolder => e.type === 'folder' && e.id === app.folderId)?.members ?? []).map(railKeyOfPin)
      : list.map(entryKey)
    const at = siblings.indexOf(key)
    const otherFolders = list.filter((e): e is RailFolder => e.type === 'folder' && e.id !== app.folderId)
    const items: FileMenuItem[] = [
      { kind: 'item', icon: SquareArrowOutUpRight, label: t('taskBar.openNewTab'), onClick: () => { closeMenu(); onOpenUserAppNewTab?.(app.kind, app.ref) } },
    ]
    if (app.pinned && siblings.length > 1) {
      items.push({ kind: 'item', icon: ArrowUp, label: t('taskBar.moveUp'), disabled: at <= 0, onClick: () => { closeMenu(); moveEntryBy(key, -1) } })
      items.push({ kind: 'item', icon: ArrowDown, label: t('taskBar.moveDown'), disabled: at < 0 || at >= siblings.length - 1, onClick: () => { closeMenu(); moveEntryBy(key, 1) } })
    }
    if (otherFolders.length > 0 && onMoveToFolder) {
      items.push({
        kind: 'submenu', icon: FolderInput, label: t('taskBar.moveToFolder'),
        open: openSubmenu === 'move', onOpen: () => setOpenSubmenu('move'), onClose: () => setOpenSubmenu(s => (s === 'move' ? null : s)),
        options: otherFolders.map(f => ({ label: f.title || t('taskBar.folder'), onClick: () => { closeMenu(); onMoveToFolder(app.kind, app.ref, f.id) } })),
      })
    }
    // CREATING a folder existed only inside the drag's combine branch, and both
    // drag hooks return early on `pointerType === 'touch'` — so a touch-only or
    // keyboard-only user could never make a first one, which left "Move to
    // folder" permanently empty for them and the whole feature unreachable.
    // This is the same gesture by another route: pick the app to group WITH,
    // exactly as dropping one tile on another does, so it needs no new API and
    // lands on the identical `onCreateFolder` path.
    const groupWith = list.filter((e): e is RailApp => e.type !== 'folder' && entryKey(e) !== key)
    if (groupWith.length > 0 && onCreateFolder) {
      items.push({
        kind: 'submenu', icon: FolderInput, label: t('taskBar.groupWith'),
        open: openSubmenu === 'group', onOpen: () => setOpenSubmenu('group'), onClose: () => setOpenSubmenu(s => (s === 'group' ? null : s)),
        options: groupWith.map(o => ({
          label: o.title || t('taskBar.app'),
          onClick: () => {
            closeMenu()
            const suggested = `${o.title || t('taskBar.app')} & ${app.title || t('taskBar.app')}`.slice(0, 60)
            void Promise.resolve(
              onCreateFolder({ kind: app.kind, ref: app.ref }, { kind: o.kind, ref: o.ref }, suggested),
            ).then(id => {
              if (!id) return
              pulse(`folder:${id}`)
              // Through `requestOpenFolder`, never a bare setter: this menu can
              // be opened from a MEMBER ROW while that folder's island is up,
              // and assigning straight across re-anchors the same component
              // with no outro — the panel teleports, which is exactly what the
              // parked-request design exists to prevent.
              requestOpenFolderRef.current({ id, renaming: true })
              announce(t('taskBar.a11y.grouped', { app: app.title || t('taskBar.app'), other: o.title || t('taskBar.app') }))
            }).catch(() => {})
          },
        })),
      })
    }
    items.push(
      app.pinned
        ? { kind: 'item', icon: PinOff, label: t('taskBar.unpin'), onClick: () => { closeMenu(); onUnpinUserApp?.(app.kind, app.ref) } }
        : { kind: 'item', icon: Pin, label: t('taskBar.pin'), onClick: () => { closeMenu(); onPinUserApp?.(app.kind, app.ref, app.title) } },
    )
    // Mini-apps only — standalone HTML has no icon spec to edit — and only when
    // the viewer has write access (a read-only viewer would hit a 403 on save).
    if (app.kind === 'miniapp' && app.canEdit && onEditUserAppIcon) {
      items.push({ kind: 'item', icon: Palette, label: t('taskBar.editIcon'), onClick: () => { closeMenu(); onEditUserAppIcon(app.kind, app.ref) } })
    }
    // A member of a folder can be sent back to the rail from its own menu —
    // the non-drag path, and the only one on touch.
    if (app.folderId && onMoveToFolder) {
      items.push({ kind: 'item', icon: FolderOutput, label: t('taskBar.removeFromFolder'), onClick: () => { closeMenu(); onMoveToFolder(app.kind, app.ref, null) } })
    }
    // A pinned-but-not-running app has no window to close — omit the item.
    if (app.running) {
      items.push({ kind: 'separator' })
      items.push({ kind: 'item', icon: X, label: t('taskBar.closeApp'), danger: true, onClick: () => { closeMenu(); onCloseUserApp?.(app.kind, app.ref) } })
    }
    return items
  }, [t, closeMenu, onOpenUserAppNewTab, onPinUserApp, onUnpinUserApp, onEditUserAppIcon, onCloseUserApp, onMoveToFolder, onCreateFolder, pulse, announce, railEntries, openSubmenu, moveEntryBy])

  // ── Folders (0195) ──────────────────────────────────────────────────
  // Which folder's popup is open, and whether its title is being edited.
  // ONE folder at a time: the popup is anchored to its tile, so two would
  // overlap. Closed by outside press / Escape (useDismiss in the popup), by
  // opening an app from it, by the well scrolling (the anchor moves), and
  // by the folder ceasing to exist (pruned — the entry lookup below fails).
  const [openFolder, setOpenFolder] = useState<{ id: string; renaming: boolean } | null>(null)
  // Declared beside `openFolder` (not beside `requestOpenFolder`, where it is
  // used) so `closeFolder` below can be defined before the menu builders that
  // call it — a `useCallback` deps array is evaluated at render time, so a
  // helper declared later would be in its temporal dead zone there.
  const [, setPendingFolder] = useState<{ id: string; renaming: boolean } | null>(null)
  /** Close the island AND drop any parked request. Bare `setOpenFolder(null)`
   *  leaves a parked folder behind, and it then springs open later — over the
   *  app the user just launched from the rail. */
  const closeFolder = useCallback(() => { setOpenFolder(null); setPendingFolder(null) }, [])
  /** Open a folder's island, closing whatever is open FIRST (see the long note
   *  further down). Declared here rather than beside that note so the menu
   *  builders above can depend on it — a `useCallback` deps array is evaluated
   *  during render, so a helper declared later sits in its temporal dead zone
   *  there. */
  const requestOpenFolder = useCallback((next: { id: string; renaming: boolean } | null) => {
    setOpenFolder((cur) => {
      // The WHOLE request is parked, not just its id. Parking the id alone
      // dropped `renaming`, so a folder created while another island was open
      // re-opened without its rename box and committed the auto-suggested
      // title with no chance to type over it — the one path this indirection
      // was added for.
      if (next && cur && cur.id !== next.id) { setPendingFolder(next); return null }
      setPendingFolder(null)
      return next
    })
  }, [])
  requestOpenFolderRef.current = requestOpenFolder
  // Every tile's element by rail key — the popup anchors to its folder's,
  // the drag rings a combine target's.
  const tileRefs = useRef(new Map<string, HTMLButtonElement>())
  const folderMenu = useCallback((folder: RailFolder): FileMenuItem[] => {
    const key = railKeyOfFolder(folder)
    const keys = (railEntries ?? []).map(entryKey)
    const at = keys.indexOf(key)
    const running = folder.members.filter(m => m.running)
    const swatch = (color: FolderColorKey | null) => (
      <span className="inline-flex items-center gap-2">
        <span aria-hidden className="inline-block w-3 h-3 rounded-full border themed-border" style={{ background: color ? FOLDER_COLOR_SWATCH[color] : 'transparent' }} />
        {t(`taskBar.colors.${color ?? 'none'}`)}
      </span>
    )
    const items: FileMenuItem[] = [
      { kind: 'item', icon: Pencil, label: t('taskBar.renameFolder'), onClick: () => { closeMenu(); requestOpenFolderRef.current({ id: folder.id, renaming: true }) } },
    ]
    if (onUpdateFolder) {
      items.push({
        kind: 'submenu', icon: Droplet, label: t('taskBar.folderColor'),
        open: openSubmenu === 'color', onOpen: () => setOpenSubmenu('color'), onClose: () => setOpenSubmenu(s => (s === 'color' ? null : s)),
        options: [null, ...FOLDER_COLOR_KEYS].map(color => ({ label: swatch(color), onClick: () => { closeMenu(); onUpdateFolder(folder.id, { color }) } })),
      })
    }
    if (keys.length > 1) {
      items.push({ kind: 'item', icon: ArrowUp, label: t('taskBar.moveUp'), disabled: at <= 0, onClick: () => { closeMenu(); moveEntryBy(key, -1) } })
      items.push({ kind: 'item', icon: ArrowDown, label: t('taskBar.moveDown'), disabled: at < 0 || at >= keys.length - 1, onClick: () => { closeMenu(); moveEntryBy(key, 1) } })
    }
    if (running.length > 0 && onCloseUserApp) {
      items.push({ kind: 'separator' })
      items.push({ kind: 'item', icon: X, label: t('taskBar.closeAll'), onClick: () => { closeMenu(); for (const m of running) onCloseUserApp(m.kind, m.ref) } })
    }
    items.push({ kind: 'separator' })
    // Not `danger`: ungrouping loses nothing — the apps return to the rail.
    items.push({ kind: 'item', icon: FolderMinus, label: t('taskBar.ungroup'), onClick: () => { closeMenu(); closeFolder(); onDeleteFolder?.(folder.id) } })
    return items
  }, [t, closeMenu, closeFolder, onDeleteFolder, onUpdateFolder, onCloseUserApp, railEntries, openSubmenu, moveEntryBy])
  buildersRef.current = { systemAppMenu, userAppMenu, folderMenu }
  const openFolderEntry = openFolder
    ? railEntries?.find((e): e is RailFolder => e.type === 'folder' && e.id === openFolder.id)
    : undefined
  // The popup's anchor is captured when it OPENS and kept while it closes, so
  // the outro plays where the panel was rather than where the tile is now.
  //
  // The one that is CLOSING is tracked separately, with its own anchor, and
  // both are rendered: opening another folder while one is open plays the
  // first's outro at its tile and the second's intro at its own. They used to
  // share one slot (`open ?? closing`), so a switch was neither — the same
  // component re-rendered with different contents, no animation ran at all,
  // and the panel simply teleported.
  const [popupAnchor, setPopupAnchor] = useState<DOMRect | null>(null)
  const [closingFolder, setClosingFolder] = useState<RailFolder | null>(null)
  const prevOpenEntry = useRef<RailFolder | undefined>(undefined)
  // LAYOUT effect, not a passive one. Closing sets `openFolder` to null, so the
  // render that follows has no open folder AND no closing one yet — the island
  // is absent from that commit and comes back in the next. A passive effect
  // lets the browser PAINT that gap: the panel vanishes for one frame and
  // reappears mid-outro, which is the blink that survived three rewrites of
  // the animation itself. A layout effect runs before the paint, so the two
  // commits are one frame on screen.
  useLayoutEffect(() => {
    if (openFolderEntry && openFolderEntry.id !== prevOpenEntry.current?.id) {
      setPopupAnchor(tileRefs.current.get(railKeyOfFolder(openFolderEntry))?.getBoundingClientRect() ?? null)
      setClosingFolder(null)
    } else if (!openFolderEntry && prevOpenEntry.current) {
      setClosingFolder(prevOpenEntry.current)
    }
    prevOpenEntry.current = openFolderEntry
    // A folder can cease to exist while open — its last member is unpinned or
    // moved out and the server prunes it. The island then closes correctly (the
    // lookup above fails), but `openFolder` KEPT the dead id, and nothing else
    // clears it. `requestOpenFolder` reads that stale `cur`, decides the next
    // click is a SWITCH, parks it behind an outro that will never play — there
    // is no island left to exit — and the folder never opens. It took a second
    // click, with the first doing nothing at all.
    if (openFolder && !openFolderEntry) closeFolder()
  }, [openFolderEntry, openFolder, closeFolder])
  const popupFolder = openFolderEntry ?? closingFolder

  /**
   * Opening a folder while another is open CLOSES THAT ONE FIRST: the request
   * is parked, the open one plays its outro, and its `onExited` starts the new
   * one's intro. Both at once (which is what rendering two islands gave) reads
   * as two panels fighting; instantly swapping the contents of one — the shape
   * before that — read as no animation at all.
   *
   * The parked id is only ever READ inside its own updater, so nothing renders
   * from it and the value half of the pair is deliberately unused: keeping it
   * in state (rather than a ref) is what makes the two setters race-free when
   * a click lands mid-outro.
   */
  /** The outro finished: drop the closing island, and open whatever was
   *  waiting on it. */
  const onIslandExited = useCallback(() => {
    setClosingFolder(null)
    setPendingFolder((parked) => { if (parked) setOpenFolder(parked); return null })
  }, [])

  // ── Drag (rail/useRailDrag.ts) ──────────────────────────────────────
  // Two kinds of target. COMBINE: an app dropped on the middle band of an app
  // makes a folder, on a folder files it (armed after the pointer rests). GAP:
  // any tile — app or folder — dropped between tiles is rearranged, and while
  // the pointer hovers a gap the tiles slide to open it and a marker shows
  // where the drop lands. The geometry is slot arithmetic over the well's
  // UNSHIFTED layout (RAIL_SLOT), never the tiles' live rects: the tiles are
  // translated while a gap is open, and measuring them would chase itself.
  const entries = railEntries ?? []
  const toast = useToastOptional()
  /** A drag from ANOTHER drive's tab names that drive; nothing here may take it (a pin of
   *  its app shows nothing, a move/link of its items fails). A drag naming no drive (an
   *  editor tab, an app tile) is this drive's own. */
  const fromOtherDrive = (transfer: { tenantId?: string }) =>
    !!transfer.tenantId && !!activeTenantId && transfer.tenantId !== activeTenantId
  /** The scroll chevrons and separator show only with apps on the rail; the well itself always renders. */
  const hasRailEntries = entries.length > 0
  const keyOfItem = (item: RailDragItem) => (item.type === 'folder' ? `folder:${item.id}` : `app:${item.kind}:${item.ref}`)
  /** Index of the dragged item on the rail (−1 for a popup row, which is not on it). */
  const sourceIndexOf = (item: RailDragItem) => (item.type === 'app' && item.fromFolderId ? -1 : entries.findIndex(e => entryKey(e) === keyOfItem(item)))
  const resolveDropTarget = useCallback((el: Element | null, item: RailDragItem, pt: { x: number; y: number }): RailDropTarget | null => {
    const root = taskBarRef.current
    const well = wellRef.current
    if (!el) return null
    // Inside a folder popup: a row of THAT folder can be rearranged among its
    // siblings. Slot arithmetic over the unshifted list, as for the rail.
    const popup = el.closest<HTMLElement>('[data-folder-popup]')
    if (popup) {
      const folderId = popup.dataset.folderPopup
      if (item.type === 'folder') return null
      // An app from the rail or from ANOTHER folder dropped anywhere on the open
      // island joins this folder — the whole panel is the target, not just the tile.
      if (item.fromFolderId !== folderId) {
        const face = islandTileRef.current
        return folderId && face ? { kind: 'combine', key: `folder:${folderId}`, el: face } : null
      }
      const listEl = popup.querySelector<HTMLElement>('[data-folder-list]')
      const members = (railEntries ?? []).find((e): e is RailFolder => e.type === 'folder' && e.id === folderId)?.members ?? []
      if (!listEl || !folderId) return null
      const lr = listEl.getBoundingClientRect()
      const src = members.findIndex(m => m.kind === item.kind && m.ref === item.ref)
      const rel = pt.y - lr.top + listEl.scrollTop
      const i = Math.max(0, Math.min(members.length - 1, Math.floor(rel / FOLDER_ROW_H)))
      const within = (rel - i * FOLDER_ROW_H) / FOLDER_ROW_H
      const j = src >= 0 && i > src ? i - 1 : i
      const g = i === src ? j : within < 0.5 ? j : j + 1
      return { kind: 'gap', scope: folderId, index: g, rect: { left: lr.left + 8, top: lr.top - listEl.scrollTop + g * FOLDER_ROW_H + (FOLDER_ROW_H - RAIL_TILE) / 2 } }
    }
    if (!root || !root.contains(el)) return null
    const list = railEntries ?? []
    const sourceIdx = sourceIndexOf(item)
    const without = list.length - (sourceIdx >= 0 ? 1 : 0)
    const toWithout = (i: number) => (sourceIdx >= 0 && i > sourceIdx ? i - 1 : i)
    const wr = well?.getBoundingClientRect()
    /** Viewport rect of slot `g` in the FINAL arrangement (without the source, gap at g). */
    const slotRect = (g: number) => ({
      left: wr ? wr.left + (wr.width - RAIL_TILE) / 2 : 8,
      top: wr && well ? wr.top + RAIL_PAD - well.scrollTop + g * RAIL_SLOT : pt.y,
    })
    if (!well || !wr) return { kind: 'gap', scope: 'rail', index: 0, rect: slotRect(0) }
    const inWell = pt.x >= wr.left && pt.x <= wr.right && pt.y >= wr.top && pt.y <= wr.bottom
    if (!inWell) {
      // Elsewhere on the rail column: above the well lands first, below lands last.
      const g = pt.y < wr.top ? 0 : without
      return { kind: 'gap', scope: 'rail', index: g, rect: slotRect(g) }
    }
    const rel = pt.y - (wr.top + RAIL_PAD) + well.scrollTop
    const i = Math.floor(rel / RAIL_SLOT)
    if (i < 0) return { kind: 'gap', scope: 'rail', index: 0, rect: slotRect(0) }
    if (i >= list.length) return { kind: 'gap', scope: 'rail', index: without, rect: slotRect(without) }
    if (i === sourceIdx) { const g = toWithout(i); return { kind: 'gap', scope: 'rail', index: g, rect: slotRect(g) } }
    const under = list[i]!
    // A row hovering its OWN folder's tile is going nowhere: not a combine
    // (it is already in there) and not a slot beside it by accident.
    if (item.type === 'app' && item.fromFolderId && under.type === 'folder' && under.id === item.fromFolderId) return null
    const within = (rel - i * RAIL_SLOT) / RAIL_TILE   // 0..1 over the tile, >1 in the gap below it
    const combinable = item.type === 'app' && within >= 0.3 && within <= 0.7
      && (under.type === 'app' || under.id !== item.fromFolderId)
    if (combinable) {
      const key = entryKey(under)
      // The open folder's rail tile is under its island; fly into the island's face.
      const tile = (under.type === 'folder' && openFolderEntry?.id === under.id ? islandTileRef.current : null) ?? tileRefs.current.get(key)
      if (tile) return { kind: 'combine', key, el: tile }
    }
    const g = within < 0.5 ? toWithout(i) : toWithout(i) + 1
    return { kind: 'gap', scope: 'rail', index: g, rect: slotRect(g) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railEntries, openFolderEntry])
  const islandTileRef = useRef<HTMLButtonElement | null>(null)
  const handleRailDrop = useCallback((item: RailDragItem, target: RailDropTarget) => {
    const list = railEntries ?? []
    if (target.kind === 'combine') {
      if (item.type !== 'app') return false
      const under = list.find(e => entryKey(e) === target.key)
      const itemLabel = labelOfKey(keyOfItem(item))
      if (under?.type === 'app') {
        // A new folder is born with a name suggested from its two members and
        // opens straight into rename with it selected — Enter keeps it, typing
        // replaces it. "Folder" as a permanent name is nobody's choice.
        const suggested = `${under.title || t('taskBar.app')} & ${itemLabel}`.slice(0, 60)
        const created = onCreateFolder?.({ kind: item.kind, ref: item.ref }, { kind: under.kind, ref: under.ref }, suggested)
        Promise.resolve(created).then(id => {
          // Announce only once the folder EXISTS. `onCreateFolder` resolves to
          // null when the write fails (its own toast says so), and announcing
          // ahead of it told a screen-reader user two apps had been grouped
          // when nothing had — the one audience with no other way to tell.
          if (!id) return
          pulse(`folder:${id}`)
          // Through `requestOpenFolder`: an island may already be open — a
          // member row can be dragged out onto a rail app, and pressing a rail
          // tile does not dismiss the island (`useDismiss` ignores rail drop
          // targets) — and assigning across re-anchors the same component with
          // no outro, which is the teleport this design exists to prevent.
          requestOpenFolder({ id, renaming: true })
          announce(t('taskBar.a11y.grouped', { app: itemLabel, other: under.title || t('taskBar.app') }))
        }).catch(() => {})
        return true
      }
      if (under?.type === 'folder') {
        onMoveToFolder?.(item.kind, item.ref, under.id)
        pulse(target.key)
        announce(t('taskBar.a11y.movedInto', { app: itemLabel, folder: under.title || t('taskBar.folder') }))
        return true
      }
      return false
    }
    const key = keyOfItem(item)
    if (target.scope !== 'rail') {
      // Inside a folder's popup: its member order.
      const folder = list.find((e): e is RailFolder => e.type === 'folder' && e.id === target.scope)
      if (!folder) return false
      const members = folder.members.map(m => railKeyOfPin(m)).filter(k => k !== key)
      const order = [...members.slice(0, target.index), key, ...members.slice(target.index)]
      const current = folder.members.map(m => railKeyOfPin(m))
      if (order.length === current.length && order.every((k, i) => k === current[i])) return false
      onReorderFolder?.(folder.id, order)
      announce(t('taskBar.a11y.moved', { item: labelOfKey(key), position: target.index + 1 }))
      return true
    }
    const keys = list.map(entryKey).filter(k => k !== key)
    const order = [...keys.slice(0, target.index), key, ...keys.slice(target.index)]
    if (item.type === 'app' && item.fromFolderId) {
      onMoveToRail?.(item.kind, item.ref, order)
      announce(t('taskBar.a11y.movedToRail', { app: labelOfKey(key), folder: labelOfKey(`folder:${item.fromFolderId}`) }))
      return true
    }
    const current = list.map(entryKey)
    if (order.length === current.length && order.every((k, i) => k === current[i])) return false   // dropped where it was
    onReorderRail?.(order, key)
    announce(t('taskBar.a11y.moved', { item: labelOfKey(key), position: target.index + 1 }))
    return true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railEntries, onCreateFolder, onMoveToFolder, onMoveToRail, onReorderRail, onReorderFolder, requestOpenFolder, t, announce, pulse, labelOfKey])
  const drag = useRailDrag({
    resolveTarget: resolveDropTarget,
    onDrop: handleRailDrop,
    // The list under the pointer scrolls: the island's while one of its slots
    // is open, the well otherwise.
    scrollContainer: () => (dragRef.current?.gap && dragRef.current.gap.scope !== 'rail'
      ? document.querySelector<HTMLElement>('[data-folder-popup] [data-folder-list]')
      : wellRef.current),
  })
  dragRef.current = drag
  // The rail is another identity's the moment the tenant or email changes:
  // a drag in flight would drop keys the new dock does not know.
  useEffect(() => { dragRef.current?.cancel() }, [activeTenantId, activeOrgEmail])
  const dragSourceKey = drag.dragging ? keyOfItem(drag.dragging) : null
  /** What the source tile does while its ghost is out. DIMMED during the drag
   *  (it is still where you left it); INVISIBLE once the pointer is up, so the
   *  ghost flying to the slot the drop just filled is the only copy on screen
   *  — two of them, one at 0.35 and one solid, converging and then swapping,
   *  is what read as a blink on release. */
  const dragSourceState = drag.settling ? 'settling' : 'true'
  const dragSourceIdx = dragSourceKey ? entries.findIndex(e => entryKey(e) === dragSourceKey) : -1
  const armedKey = drag.armedKey
  /** How many slots tile `i` (in the rendered list) slides while a gap is open:
   *  the tiles after the source close its hole, the tiles at or after the gap
   *  open one — so every tile ends exactly where the drop will put it, and the
   *  re-render after the drop changes nothing visible. */
  const railGap = drag.gap?.scope === 'rail' ? drag.gap.index : null
  /** What the drop will put in the open slot: the dragged item's own glyph. */
  const landingGhost = (() => {
    const item = drag.dragging
    if (!item) return null
    if (item.type === 'folder') {
      const f = entries.find((e): e is RailFolder => e.type === 'folder' && e.id === item.id)
      return f ? <FolderGlyph members={f.members} color={f.color} fallbackName={t('taskBar.app')} /> : null
    }
    const app = railApps(entries).find(a => a.kind === item.kind && a.ref === item.ref)
    if (!app) return null
    return app.kind === 'miniapp'
      ? <MiniappIcon spec={app.icon ?? null} name={app.title || t('taskBar.app')} size={RAIL_APP_GLYPH} refId={app.ref} rev={app.iconRev ?? null} />
      : <Globe size={RAIL_HTML_GLYPH} />
  })()
  const shiftFor = (i: number) => {
    const g = railGap
    if (g === null || i === dragSourceIdx) return 0
    const j = dragSourceIdx >= 0 && i > dragSourceIdx ? i - 1 : i
    return (j >= g ? 1 : 0) - (dragSourceIdx >= 0 && i > dragSourceIdx ? 1 : 0)
  }
  // ── A FILE dragged in (HTML5 DnD from the drive or the home grid) ────────
  // A `.pdapp` folder or an `.html` file dropped on the well is pinned into
  // the slot under the pointer. Same slot arithmetic as the pointer drag,
  // driven by the dragover's clientY; the landing ghost shows a generic app.
  const [fileGap, setFileGap] = useState<number | null>(null)
  const fileSlotAt = useCallback((clientY: number) => {
    const well = wellRef.current
    if (!well) return 0
    const wr = well.getBoundingClientRect()
    const rel = clientY - (wr.top + RAIL_PAD) + well.scrollTop
    return Math.max(0, Math.min(entries.length, Math.round(rel / RAIL_SLOT)))
  }, [entries.length])
  const handleFileDragOver = useCallback((e: React.DragEvent) => {
    if (!onPinToSlot || !isPapanDrag(e.dataTransfer)) return
    e.preventDefault()
    // A link where the drag allows one; a drive drag allows only copy/move (#605).
    e.dataTransfer.dropEffect = railFileDropEffect(e.dataTransfer.effectAllowed)
    setFileGap(fileSlotAt(e.clientY))
  }, [onPinToSlot, fileSlotAt])
  // A drag cancelled (Escape) or dropped elsewhere can end without a dragleave
  // reaching the scroller; the landing ghost must not outlive the drag.
  const fileDragActive = fileGap !== null
  useEffect(() => {
    if (!fileDragActive) return
    const clear = () => setFileGap(null)
    window.addEventListener('dragend', clear, true)
    window.addEventListener('drop', clear, true)
    return () => {
      window.removeEventListener('dragend', clear, true)
      window.removeEventListener('drop', clear, true)
    }
  }, [fileDragActive])
  const handleFileDrop = useCallback((e: React.DragEvent) => {
    setFileGap(null)
    if (!onPinToSlot || !isPapanDrag(e.dataTransfer)) return
    // Cancelled even when this tab can't resolve the drag: an uncancelled drop of a link
    // drag (Alt held, #750) can make the browser follow the link.
    e.preventDefault()
    e.stopPropagation()
    const transfer = readPapanDrag(e.dataTransfer)
    if (!transfer) return
    if (fromOtherDrive(transfer)) { toast?.error(t('taskBar.crossDrivePin')); return }
    const files = transfer.files
    const slot = fileSlotAt(e.clientY)
    const keys = entries.map(entryKey)
    let at = slot
    const pinned: Array<{ kind: 'miniapp' | 'html'; ref: string; title: string }> = []
    for (const f of files) {
      const kind = f.isFolder && isMiniappFolder(f.name) ? 'miniapp' : !f.isFolder && /\.html?$/i.test(f.name) ? 'html' : null
      if (!kind) continue
      const key = `app:${kind}:${f.id}`
      if (keys.includes(key)) continue
      keys.splice(at, 0, key)
      pinned.push({ kind, ref: f.id, title: f.name.replace(/\.(pdapp|miniapp|pda|html?)$/i, '') })
      at++
    }
    // ONE call for the whole drop: the order is written once, after every pin
    // exists. Per-file calls each sent the whole list, so dropping three files
    // fired three unsequenced whole-list writes and whichever landed last won.
    if (pinned.length) onPinToSlot(pinned, keys)
  }, [onPinToSlot, entries, fileSlotAt, activeTenantId, toast, t])

  const tileWrapStyle = (i: number) => {
    const shift = shiftFor(i)
    return {
      transform: shift ? `translateY(${shift * RAIL_SLOT}px)` : undefined,
      transition: drag.dragging && !drag.settling ? RAIL_SHIFT_TRANSITION : undefined,
    }
  }

  const [isWorkspaceDropTarget, setIsWorkspaceDropTarget] = useState(false)
  const [isDriveDropTarget, setIsDriveDropTarget] = useState(false)
  const [showWorkspacePopup, setShowWorkspacePopup] = useState(false)
  const workspaceButtonRef = useRef<HTMLButtonElement>(null)
  const taskBarRef = useRef<HTMLDivElement>(null)

  // The app well fades at both ends, but ONLY while it actually overflows — a
  // permanent mask dims the first and last tile of a list that fits, which
  // reads as a rendering fault rather than an affordance.
  //
  // A ResizeObserver rather than a render-time measurement: the well's height
  // comes from the viewport, so it changes on window resize and on OS zoom
  // with no React state involved.
  const wellRef = useRef<HTMLDivElement>(null)
  // The scroll cues, one per end. Same answer as that end's fade, written the
  // same way — onto the element, never React state, since it changes on every
  // scroll frame and a re-render of the whole rail per frame is the cost.
  const moreAboveRef = useRef<HTMLDivElement>(null)
  const moreBelowRef = useRef<HTMLDivElement>(null)
  /** The down chevron's pill — painted outside the well, where the mask cannot fade it; its target is `moreBelowRef`, inside. */
  const moreBelowPaintRef = useRef<HTMLSpanElement>(null)
  // The list AT REST — its tiles' height, one tile, and the gap between rows —
  // measured whenever the layout can have changed (mount, a pin change, a
  // resize) and read on every scroll frame.
  const restRef = useRef({ tiles: 0, tile: RAIL_TILE, gap: RAIL_GAP })
  /** Where the chevrons' last animated scroll was headed — a click's page or a wheel notch — so the next step continues it (rail/wheel.ts). */
  const scrollAimRef = useRef<ScrollAim | null>(null)
  const railCount = railEntries?.length ?? 0
  const lastRailKey = railCount ? entryKey(railEntries![railCount - 1]!) : null
  // A LAYOUT effect: the first measurement lands before the first paint, so an
  // overflowing rail never paints once without its fade and chevrons and then
  // gains them a frame later. It writes attributes only — the mask and the
  // chevrons' visibility, neither of which moves a box — so it cannot feed back
  // into what it measures, and a ResizeObserver delivery never resizes anything.
  useLayoutEffect(() => {
    const el = wellRef.current
    if (!el) return
    const last = lastRailKey ? tileRefs.current.get(lastRailKey)?.parentElement ?? null : null
    const measure = () => {
      // From layout, not the live scroll height: offsets ignore transforms, so a
      // drag's slot shifts and a folder's absorb pulse do not count, and a file
      // drop's landing ghost is not a tile — the scroll height counts all three
      // and fires nothing when they go away. And measured, not computed from the
      // geometry constants: the tiles are sized in rem, so the browser's font
      // size scales the whole list.
      const style = getComputedStyle(el)
      restRef.current = last
        ? {
            tiles: last.offsetTop + last.offsetHeight + (Number.parseFloat(style.paddingBottom) || 0),
            tile: last.offsetHeight,
            gap: Number.parseFloat(style.rowGap) || 0,
          }
        : { tiles: 0, tile: RAIL_TILE, gap: RAIL_GAP }
    }
    const sync = () => {
      const { tiles, tile, gap } = restRef.current
      const overflowing = tiles > el.clientHeight + 1
      el.dataset.overflowing = String(overflowing)
      // While it overflows, the list ends in the down chevron's own slot (its
      // target, `.ws-rail-more-hit`, shown by that flag): that is where the target
      // rests once the list is scrolled to the end, so a click following it there
      // lands on nothing rather than on the last app. Read after the flag is
      // written; the slot exists only then.
      const below = moreBelowRef.current
      const content = overflowing && below ? tiles + gap + below.offsetHeight : tiles
      // Each end fades only while there is something PAST it. A fade that
      // survives being scrolled to the end takes ~12px off the last 40px tile
      // and goes on promising more below, so a fully-scrolled rail reads as
      // holding one more app than it does — the tile looks tucked under the
      // avatar. That is the same "rendering fault rather than an affordance"
      // the conditional mask already exists to avoid, one step further in:
      // the first version asked whether the list overflows, this one asks
      // whether it overflows IN THAT DIRECTION.
      el.dataset.fadeTop = String(overflowing && el.scrollTop > 0)
      el.dataset.fadeBottom = String(overflowing && el.scrollTop < content - el.clientHeight - 1)
      // The fade alone was not enough of a cue: on the dark themes it reads as the
      // icon's own shading. A chevron says "more this way" in any theme and at any
      // height, and scrolls there when clicked.
      const room = el.clientHeight >= RAIL_CHEVRON_MIN_ROWS * (tile + gap)
      // When one goes, note when: a finger tapping where it was a moment ago is
      // still taken by it (see the rail's touch handlers). Its tooltip is there
      // only while it shows.
      const target = (node: HTMLElement | null, fade: boolean) => {
        if (!node) return
        const show = room && fade
        if (node.dataset.show === 'true' && !show) node.dataset.hiddenAt = String(performance.now())
        node.dataset.show = String(show)
        const title = show ? node.dataset.label ?? '' : ''
        if (node.title !== title) node.title = title
      }
      target(moreAboveRef.current, el.dataset.fadeTop === 'true')
      target(below, el.dataset.fadeBottom === 'true')
      if (moreBelowPaintRef.current && below) moreBelowPaintRef.current.dataset.show = below.dataset.show
      // A scroll back against a chevron's pending step (the list's own wheel) ends it.
      scrollAimRef.current = followScroll(scrollAimRef.current, el.scrollTop)
    }
    const remeasure = () => { measure(); sync() }
    remeasure()
    // Scrolling changes the answer while moving no box, so the ResizeObserver
    // below cannot see it — this listener is what makes the fade track the
    // position rather than just the content.
    el.addEventListener('scroll', sync, { passive: true })
    const dropScroll = () => el.removeEventListener('scroll', sync)
    if (typeof ResizeObserver === 'undefined') return dropScroll
    // The well's size (a window resize, OS zoom) and the last tile's (the font
    // size, which scales every tile) are what can move the list at rest.
    const ro = new ResizeObserver(remeasure)
    ro.observe(el)
    if (last) ro.observe(last)
    return () => { dropScroll(); ro.disconnect() }
  // Keyed on what can move the tiles — how many there are and which is last — not
  // on the entries array, whose identity changes on every render of the shell
  // (a query hook returns a new array each time): the effect re-measured, with two
  // forced layouts, on every navigation.
  }, [railCount, lastRailKey])
  // Any HTML5 drag in the window — a file from the drive, one from the desktop —
  // hides the chevrons and makes them click-through until it ends, so every
  // dragover and drop over one reaches the well beneath it: the chevrons are not
  // inside the well, and a drop landing on one would be lost. A drag that leaves
  // the window ends with no event here; while one is live, dragover repeats (at
  // least every ~550ms by the spec), so a quiet second means it is over.
  useEffect(() => {
    const root = taskBarRef.current
    if (!root) return
    let quiet: ReturnType<typeof setTimeout> | undefined
    const end = () => { clearTimeout(quiet); delete root.dataset.htmlDrag }
    const live = () => {
      if (root.dataset.htmlDrag !== 'true') root.dataset.htmlDrag = 'true'
      clearTimeout(quiet)
      quiet = setTimeout(end, HTML_DRAG_QUIET_MS)
    }
    window.addEventListener('dragenter', live, true)
    window.addEventListener('dragover', live, true)
    window.addEventListener('drop', end, true)
    window.addEventListener('dragend', end, true)
    return () => {
      end()
      window.removeEventListener('dragenter', live, true)
      window.removeEventListener('dragover', live, true)
      window.removeEventListener('drop', end, true)
      window.removeEventListener('dragend', end, true)
    }
  }, [])

  const scrollWellBy = (dy: number, animate: boolean) => {
    const el = wellRef.current
    if (!el) return
    const step = scrollStep(dy, el, scrollAimRef.current, performance.now(), animate, prefersReducedMotion())
    scrollAimRef.current = step.aim
    el.scrollTo({ top: step.top, behavior: step.smooth ? 'smooth' : 'auto' })
  }
  /**
   * A chevron's click: a page that brings the next tiles in clear of BOTH fades.
   * The window a tile is clear in is the well less a fade at each end and the tile
   * itself; paging by exactly that much, every tile stands clear in some view.
   * Paging by the whole well less a row put a tile or two in a fade in every view.
   * Never less than a row.
   */
  const scrollWell = (direction: -1 | 1) => {
    const el = wellRef.current
    if (!el) return
    const { tile, gap } = restRef.current
    scrollWellBy(direction * Math.max(tile + gap, el.clientHeight - 2 * railFadeLength(el.clientHeight) - tile), true)
  }
  /**
   * A wheel turned over a chevron scrolls the list it points into — the chevron
   * is not inside the well, so the browser would scroll nothing. A mouse notch
   * animates as it would over the list; a trackpad's small deltas land as they
   * come, unless they continue an animated step.
   */
  const forwardWheel = (e: React.WheelEvent) => {
    const el = wellRef.current
    const dy = el ? wheelDeltaPx(e, el.clientHeight) : null
    if (dy !== null) scrollWellBy(dy, Math.abs(dy) >= WHEEL_NOTCH_PX)
  }
  /**
   * A MOUSE press on a chevron: down and up on it, the primary button, while it
   * shows. Not an `onClick` — React gives any element with one a native click
   * handler (for iOS Safari), and that alone makes the chevron a touch-adjustment
   * candidate (below). Pressing keeps focus where it was.
   */
  const pressRef = useRef<EventTarget | null>(null)
  // A press that ends anywhere but its chevron is over: any new mousedown starts from
  // nothing (capture — before the chevron's own handler records one), and any
  // mouseup ends it (bubble — after the chevron's own handler has read it). Without
  // this, pressing a chevron and releasing off it left the press standing, and a
  // later release on the chevron paged the list.
  useEffect(() => {
    const clear = () => { pressRef.current = null }
    window.addEventListener('mousedown', clear, true)
    window.addEventListener('mouseup', clear)
    return () => {
      window.removeEventListener('mousedown', clear, true)
      window.removeEventListener('mouseup', clear)
    }
  }, [])
  const chevronPress = (direction: -1 | 1) => ({
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      pressRef.current = e.button === 0 ? e.currentTarget : null
    },
    onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => {
      const pressed = pressRef.current === e.currentTarget
      pressRef.current = null
      if (pressed && e.button === 0 && e.currentTarget.dataset.show === 'true') scrollWell(direction)
    },
  })
  /** A pill's hover colour, set from the mouse: a `:hover` style would make its chevron a touch-adjustment target (below). */
  const pillHover = (pill: HTMLElement | null, on: boolean) => {
    if (!pill) return
    if (on) pill.dataset.hover = 'true'
    else delete pill.dataset.hover
  }
  /**
   * FINGER TAPS on the chevrons, judged by where the finger landed — here at the
   * rail, whichever element the browser gave the touch to. Chromium's touch
   * adjustment moves a tap's target (the touchstart and the click alike, though not
   * its coordinates) to the best nearby control, and favours small ones: a chevron
   * that qualified took taps on the edges of Settings, the first app and the
   * avatar, and one that did not lost taps on its own edges to them (code review).
   * So neither chevron is a candidate at all — a div, no `:hover` style, not
   * focusable, no click handler — and a tap whose touch point lies in a chevron's box is taken here,
   * cancelling the touchend so the adjusted click reaches nothing. A chevron that
   * just went (its end reached) takes a tap a moment longer, so a finger tapping it
   * again opens nothing; after that its space is the page's again, as it always was.
   *
   * What counts as a tap is the BROWSER's call, not a slop of ours: the touchend is
   * cancelable exactly while the gesture is still going to click (Chromium allows
   * ~15px of drift and a long hold), and stops being cancelable once it has become a
   * scroll — which is then the gesture, left alone. A narrower slop of our own left
   * taps between the two limits to the browser, which clicked what lay beside the
   * chevron (code review). Only touches that start in the rail itself count: the
   * folder island is portaled out of it, but React still delivers its touches here.
   */
  const touchRef = useRef<{ x: number; y: number } | null>(null)
  const onRailTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    const inRail = taskBarRef.current?.contains(e.target as Node) ?? false
    touchRef.current = inRail && e.touches.length === 1 && t ? { x: t.clientX, y: t.clientY } : null
  }
  const onRailTouchEnd = (e: React.TouchEvent) => {
    const start = touchRef.current
    touchRef.current = null
    if (!start || !e.cancelable || e.touches.length > 0 || drag.dragging || taskBarRef.current?.dataset.htmlDrag) return
    for (const [node, direction] of [[moreAboveRef.current, -1], [moreBelowRef.current, 1]] as const) {
      if (!node) continue
      const r = node.getBoundingClientRect()
      if (r.height === 0 || start.x < r.left || start.x > r.right || start.y < r.top || start.y > r.bottom) continue
      const shown = node.dataset.show === 'true'
      if (!shown && performance.now() - Number(node.dataset.hiddenAt ?? Number.NEGATIVE_INFINITY) >= CHEVRON_GRACE_MS) return
      e.preventDefault()
      // A tap the rail takes is still a press outside any open menu: the menus
      // close on mousedown, which cancelling the touchend also cancelled. Sent from
      // the chevron's parent, so the chevron's own press handler does not see it.
      node.parentElement?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }))
      if (shown) scrollWell(direction)
      return
    }
  }
  /**
   * Keyboard focus landing on a tile in a faded band scrolls it clear of the
   * fade. A tile fully inside the well is "in view" to the browser, so without
   * this Tab could stop on an icon drawn at a sliver of its opacity. Keyboard
   * only (`:focus-visible`): a click also focuses, and scrolling the list under a
   * pointer mid-press would move the button out from under the click. Tiles
   * only: a browser that makes the scrollable well itself a tab stop must not
   * shift the list when it lands there.
   */
  const revealFocusedTile = (e: React.FocusEvent<HTMLDivElement>) => {
    const el = wellRef.current
    const target = e.target as HTMLElement
    if (!el || target === el || !isKeyboardFocus(target)) return
    const wr = el.getBoundingClientRect()
    const r = target.getBoundingClientRect()
    const band = railFadeLength(el.clientHeight)
    const intoTop = r.top - (wr.top + (el.dataset.fadeTop === 'true' ? band : 0))
    const intoBottom = r.bottom - (wr.bottom - (el.dataset.fadeBottom === 'true' ? band : 0))
    if (intoTop < 0) el.scrollBy({ top: intoTop })
    else if (intoBottom > 0) el.scrollBy({ top: intoBottom })
  }
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const driveHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  // Opening waits for the pointer to REST on the icon (WORKSPACE_POPUP_OPEN_DELAY_MS) — sweeping across the rail no
  // longer flashes the popup open. Leaving, clicking or hovering another icon cancels a pending open.
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelOpen = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])
  const scheduleOpen = useCallback((alreadyOpen: boolean) => {
    cancelOpen()
    // Coming back from the open popup (or across the gap to it) re-enters at once — only the FIRST open waits.
    if (alreadyOpen) { setShowWorkspacePopup(true); return }
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      if (!dragRef.current?.dragging) setShowWorkspacePopup(true)
    }, WORKSPACE_POPUP_OPEN_DELAY_MS)
  }, [cancelOpen])
  useEffect(() => cancelOpen, [cancelOpen])

  const scheduleClose = useCallback(() => {
    cancelClose()
    cancelOpen()
    // Small grace period so the mouse can travel from button to popup across the gap
    closeTimerRef.current = setTimeout(() => setShowWorkspacePopup(false), 150)
  }, [cancelClose, cancelOpen])

  // Is the pointer inside the hover panel right now? Set from the panel's own
  // enter/leave. `dragend` (below) needs this: it cannot ask the DOM, and
  // "did the cursor enter?" is not the same question as "is the cursor here?".
  const pointerInPanelRef = useRef(false)
  const enterPanel = useCallback(() => { pointerInPanelRef.current = true; cancelClose() }, [cancelClose])
  const leavePanel = useCallback(() => { pointerInPanelRef.current = false; scheduleClose() }, [scheduleClose])

  // Clean up drop highlights and popup when drag ends (Escape key or drop outside).
  // Note: right-click cannot cancel HTML5 drag in Chrome — Chrome suppresses all
  // opposite-button events while a drag is active. Escape is the standard cancel.
  useEffect(() => {
    const handleDragCleanup = () => {
      setIsWorkspaceDropTarget(false)
      setIsDriveDropTarget(false)
      if (driveHoverTimerRef.current) {
        clearTimeout(driveHoverTimerRef.current)
        driveHoverTimerRef.current = null
      }
      // Popup may have been opened by dragEnter — schedule close since mouseLeave
      // won't fire after drag-cancel.
      //
      // …UNLESS the pointer is inside the panel, which is where a card reorder
      // ends. This used to close unconditionally, on the assumption that "if the
      // cursor is still over the popup, mouseEnter will cancelClose()". It
      // doesn't: mouseenter fires on ENTERING, and after a drop the cursor never
      // left — so nothing cancelled it and the panel vanished ~150ms after every
      // successful move. `dragend` also fires AFTER `drop`, so the panel's own
      // onDrop→cancelClose couldn't save it either.
      if (!pointerInPanelRef.current) scheduleClose()
    }
    window.addEventListener('dragend', handleDragCleanup)
    return () => window.removeEventListener('dragend', handleDragCleanup)
  }, [scheduleClose])

  const handleWorkspaceDragEnter = (e: React.DragEvent) => {
    if (isPapanDrag(e.dataTransfer)) {
      e.preventDefault()
      cancelClose()
      // Never while a rail drag is in flight: the panel would fog the app the
      // moment the pointer crossed this button on its way somewhere else.
      if (!dragRef.current?.dragging) setShowWorkspacePopup(true)
      setIsWorkspaceDropTarget(true)
    }
  }

  const handleWorkspaceDragOver = (e: React.DragEvent) => {
    if (isPapanDrag(e.dataTransfer)) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      // Still over the button: undo any close a leave into its own glyph scheduled.
      cancelClose()
    }
  }

  const handleWorkspaceDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    // dragleave fires on every boundary the pointer crosses, the button's own icon
    // included (a mouse hover's mouseleave doesn't) — only a real leave closes.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
    setIsWorkspaceDropTarget(false)
    // Schedule popup close — if the cursor enters the popup, its onDragOver
    // calls cancelClose() so the popup stays open for dropping on workspace items.
    scheduleClose()
  }

  const handleWorkspaceDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsWorkspaceDropTarget(false)

    const transfer = readPapanDrag(e.dataTransfer)
    if (transfer && fromOtherDrive(transfer)) { toast?.error(t('taskBar.crossDriveMove')); return }
    if (transfer && onWorkspaceDrop && transfer.files.length > 0) onWorkspaceDrop(transfer.files)
  }

  const handleDriveDragEnter = (e: React.DragEvent) => {
    if (isPapanDrag(e.dataTransfer) || e.dataTransfer.types.includes('Files')) {
      e.preventDefault()
      setIsDriveDropTarget(true)
      if (driveHoverTimerRef.current) clearTimeout(driveHoverTimerRef.current)

      if (activeView !== 'drive') {
        // Not on drive → 200ms to switch to DriveView
        driveHoverTimerRef.current = setTimeout(() => {
          setIsDriveDropTarget(false)
          onViewChange('drive')
        }, 200)
      } else {
        // Already on drive → 1s to navigate to root folder
        driveHoverTimerRef.current = setTimeout(() => {
          setIsDriveDropTarget(false)
          onDriveDrop?.([])
        }, 1000)
      }
    }
  }

  const handleDriveDragOver = (e: React.DragEvent) => {
    if (isPapanDrag(e.dataTransfer) || e.dataTransfer.types.includes('Files')) {
      e.preventDefault()
      e.dataTransfer.dropEffect = isPapanDrag(e.dataTransfer) ? 'move' : 'copy'
    }
  }

  const handleDriveDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDriveDropTarget(false)
    if (driveHoverTimerRef.current) {
      clearTimeout(driveHoverTimerRef.current)
      driveHoverTimerRef.current = null
    }
  }

  const handleDriveIconDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDriveDropTarget(false)
    if (driveHoverTimerRef.current) {
      clearTimeout(driveHoverTimerRef.current)
      driveHoverTimerRef.current = null
    }

    // Internal Papan file move → root folder
    if (isPapanDrag(e.dataTransfer)) {
      const transfer = readPapanDrag(e.dataTransfer)
      if (transfer && fromOtherDrive(transfer)) { toast?.error(t('taskBar.crossDriveMove')); return }
      if (transfer && onDriveDrop && transfer.files.length > 0) onDriveDrop(transfer.files)
      return
    }

    // External desktop files → upload to root folder
    if (e.dataTransfer.files?.length > 0 && onDriveUpload) {
      onDriveUpload(e.dataTransfer.files)
    }
  }

  // Bin + quarantine live inside the drive shell's side panel now — the
  // taskbar only carries app-level surfaces. Chat home has no item here:
  // the LOGO is its button (home page).
  const keybookAvailable = useKeybookAvailable()
  const allItems: TaskBarItem[] = [
    { id: 'drive', icon: HardDrive, labelKey: 'taskBar.drive' },
    { id: 'workspace', icon: LayoutGrid, labelKey: 'taskBar.workspace' },
    // Every PDApp the viewer can open, most recently opened first — a quick
    // launcher. Managing apps stays in the drive's PDApps page.
    { id: 'apps', icon: PDAppIcon, labelKey: 'taskBar.pdapps' },
    { id: 'keybook', icon: BookKey, labelKey: 'taskBar.keybook' },
    // Usage & Billing is the PLATFORM shelf — every tenant's ceiling, feature
    // pricing, the catalog. An org admin's own budget is Settings →
    // Organization and a member's own spend is Settings → Usage, so neither
    // gets an entry point here.
    ...(showBilling === false ? [] : [{ id: 'billing' as View, icon: Gauge, labelKey: 'taskBar.platform' }]),
    { id: 'settings', icon: Settings, labelKey: 'taskBar.settings' },
  ]
  // Annotated ABOVE and filtered here, not `[...].filter()` on one expression:
  // the annotation would then describe the filter's result and the literal
  // would infer `id: string`, which does not satisfy `View`.
  const items = allItems.filter(
    item => !(identityOnly && IDENTITY_ONLY_HIDDEN_VIEWS.includes(item.id))
      // Ambient, not a prop (0179): the switch is deployment-global, so a
      // future mount of this component cannot forget to thread it the way a
      // default-open prop could. `identityOnly` stays a prop because it is
      // caller/tenant state.
      && !(!keybookAvailable && item.id === 'keybook'),
  )

  /** One app tile — on the rail, or (via the popup) inside a folder. */
  const renderAppTile = (app: RailApp, index: number) => {
    const Icon = app.kind === 'miniapp' ? PDAppIcon : Globe
    const owner = `app:${app.kind}:${app.ref}`
    // Mini-apps render the real squircle icon (spec or name-derived
    // default); standalone HTML keeps the generic Globe glyph.
    const glyph = app.kind === 'miniapp'
      ? <MiniappIcon spec={app.icon ?? null} name={app.title || t('taskBar.app')} size={RAIL_APP_GLYPH} refId={app.ref} rev={app.iconRev ?? null} className="pointer-events-none" />
      : <Icon size={RAIL_HTML_GLYPH} className="pointer-events-none" />
    return (
      <div key={`${app.kind}:${app.ref}`} className="group relative" style={tileWrapStyle(index)}>
        <GripVertical size={12} className="ws-rail-grip absolute -left-2 top-1/2 -translate-y-1/2 themed-text-muted" aria-hidden />
        <button
          ref={(el) => { if (el) tileRefs.current.set(owner, el); else tileRefs.current.delete(owner) }}
          data-menu-owner={owner}
          data-rail-drop={owner}
          data-drag-source={dragSourceKey === owner ? dragSourceState : undefined}
          data-drop-armed={armedKey === owner ? 'true' : undefined}
          onPointerDown={(e) => { startHold(owner, e, () => buildersRef.current.userAppMenu(app)); drag.onPointerDown(e, { type: 'app', kind: app.kind, ref: app.ref, fromFolderId: null }, () => glyph) }}
          onPointerMove={moveHold}
          onPointerUp={cancelHold}
          onPointerCancel={cancelHold}
          onPointerLeave={cancelHold}
          onClick={(e) => {
            if (holdFired.current) { holdFired.current = false; return }
            if (pressConsumedRef.current) { pressConsumedRef.current = false; return }
            if (drag.consumeClick()) return
            if (app.active) { openIconMenu(owner, e.currentTarget, () => buildersRef.current.userAppMenu(app)); return }
            closeFolder()   // an open island closes when an app is opened from the rail
            onOpenUserApp?.(app.ref)
          }}
          onContextMenu={(e) => { e.preventDefault(); if (drag.dragging) return; openIconMenu(owner, e.currentTarget, () => buildersRef.current.userAppMenu(app)) }}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors relative cursor-grab active:cursor-grabbing ${
            app.active
              ? 'ws-rail-active themed-icon-active'
              : 'themed-icon hover:themed-icon-active hover:themed-bg-tertiary'
          }`}
          data-tooltip={`${app.title || t('taskBar.app')}${app.writeFailures ? ` — ${t('taskBar.writesFailed')}` : ''} — ${t('taskBar.dragHint')}`}
          data-tooltip-placement="right"
          aria-label={app.writeFailures ? `${app.title || t('taskBar.app')} (${t('taskBar.writesFailed')})` : app.title || t('taskBar.app')}
        >
          {glyph}
          {app.writeFailures ? <WriteFailureDot /> : null}
          {/* Active → bar; running-but-inactive → dot; pinned-idle → nothing. */}
          <RailIndicator state={app.active ? 'active' : app.running ? (app.asleep ? 'asleep' : 'running') : 'idle'} />
        </button>
        {/* Close now lives in the icon context menu (right-click /
            long-press / second-click-active), not a hover ✕. */}
      </div>
    )
  }

  /** One folder tile: a 2×2 glyph of its members; click toggles the popup. */
  const renderFolderTile = (folder: RailFolder, index: number) => {
    const owner = `folder:${folder.id}`
    const isOpen = openFolder?.id === folder.id
    const title = folder.title || t('taskBar.folder')
    // The tooltip names the members — the glyph shows four at most.
    const tooltip = `${title}: ${folder.members.map(m => m.title || t('taskBar.app')).join(', ')} — ${t('taskBar.dragHint')}`
    return (
      <div key={owner} className="group relative" style={tileWrapStyle(index)}>
        <GripVertical size={12} className="ws-rail-grip absolute -left-2 top-1/2 -translate-y-1/2 themed-text-muted" aria-hidden />
        <button
          ref={(el) => { if (el) tileRefs.current.set(owner, el); else tileRefs.current.delete(owner) }}
          data-menu-owner={owner}
          data-rail-folder={folder.id}
          data-rail-drop={owner}
          data-drag-source={dragSourceKey === owner ? dragSourceState : undefined}
          data-drop-armed={armedKey === owner ? 'true' : undefined}
          data-absorb={absorbKey === owner ? 'true' : undefined}
          onPointerDown={(e) => { startHold(owner, e, () => buildersRef.current.folderMenu(folder)); drag.onPointerDown(e, { type: 'folder', id: folder.id }, () => <FolderGlyph members={folder.members} color={folder.color} fallbackName={t('taskBar.app')} />) }}
          onPointerMove={moveHold}
          onPointerUp={cancelHold}
          onPointerCancel={cancelHold}
          onPointerLeave={cancelHold}
          onClick={() => {
            if (holdFired.current) { holdFired.current = false; return }
            if (pressConsumedRef.current) { pressConsumedRef.current = false; return }
            if (drag.consumeClick()) return
            requestOpenFolder(isOpen ? null : { id: folder.id, renaming: false })
          }}
          onContextMenu={(e) => { e.preventDefault(); if (drag.dragging) return; openIconMenu(owner, e.currentTarget, () => buildersRef.current.folderMenu(folder)) }}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors relative cursor-grab active:cursor-grabbing ${
            folder.active || isOpen
              ? 'ws-rail-active themed-icon-active'
              : 'themed-icon hover:themed-icon-active hover:themed-bg-tertiary'
          }`}
          data-tooltip={tooltip}
          data-tooltip-placement="right"
          aria-label={`${title} (${t('taskBar.appsInFolder', { count: folder.members.length })})${folder.members.some((m) => m.writeFailures) ? ` — ${t('taskBar.writesFailed')}` : ''}`}
          aria-expanded={isOpen}
        >
          <FolderGlyph members={folder.members} color={folder.color} fallbackName={t('taskBar.app')} />
          {folder.members.some((m) => m.writeFailures) ? <WriteFailureDot /> : null}
          {/* Running state is the INDICATOR, the same vocabulary an app tile
              uses. A count badge on top of it was a second thing saying the
              same thing in a different language, on the one tile that already
              shows its members' own icons. */}
          <RailIndicator state={folder.active ? 'active' : folder.running ? (folder.members.every((m) => !m.running || m.asleep) ? 'asleep' : 'running') : 'idle'} />
        </button>
      </div>
    )
  }

  return (
    // THREE ZONES, and only the middle one is content.
    //
    // This used to be one flex column holding system items, user apps, a
    // `flex-1` spacer, the avatar and the bell — with no `overflow`, no
    // `min-h-0`, and no `shrink-0` on any tile. Every child of a flex column
    // shrinks by default, so a user with enough apps open did not get a
    // scrollbar: the 40px tiles compressed into ovals, and once they could not
    // compress further the spacer collapsed and the avatar and bell were
    // pushed past the bottom edge. The cost of running many apps was losing
    // the two controls that have nothing to do with apps.
    //
    // Now the system items and the account controls are fixed zones
    // (`shrink-0`) and the apps live in their own scroller between them. The
    // `min-h-0` on that scroller is load-bearing: a flex child refuses to
    // shrink below its content without it, which is the original bug wearing
    // a different hat.
    // `data-rail-drop="well"` on the ROOT as well as the well: a popup row
    // dragged out returns to the rail when dropped anywhere on the rail
    // column that is not an app or folder tile — the 4px gaps between tiles
    // are not a target anyone can hit.
    <div ref={taskBarRef} data-rail-drop="well" data-rail-armed={armedKey ?? undefined} data-rail-gap={railGap ?? undefined} data-rail-dragging={drag.dragging ? 'true' : undefined} data-taskbar className="w-14 ws-taskbar flex flex-col items-center py-2" onTouchStartCapture={onRailTouchStart} onTouchEndCapture={onRailTouchEnd}>
      {/* Logo mark = the HOME button. WHICH surface that is comes from the
          deployment's brand config (`homeView`, default 'chat'): a launcher
          deployment lands on the app grid, so its logo must go there too —
          otherwise the most prominent button in the app takes you somewhere
          other than where you just landed, with no way back but the URL.
          Home is the base surface, so it gets no close affordance and no
          running dot; a soft recess marks it active. Carries the same icon
          context menu as the system apps (right-click / long-press /
          second-click-active) — home is never closable, so the menu is just
          "Open in new tab".

          One value drives the target AND the active mark, deliberately: as two
          they can disagree, and the failure is a logo that navigates home and
          then does not look active there. */}
      <div className="flex flex-col items-center gap-1 shrink-0">
      <button
        data-menu-owner={`sys:${homeView}`}
        onPointerDown={(e) => startHold(`sys:${homeView}`, e, () => buildersRef.current.systemAppMenu(homeView))}
        onPointerMove={moveHold}
        onPointerUp={cancelHold}
        onPointerCancel={cancelHold}
        onPointerLeave={cancelHold}
        onClick={(e) => {
          if (holdFired.current) { holdFired.current = false; return }
          if (pressConsumedRef.current) { pressConsumedRef.current = false; return }
          if (activeView === homeView) { openIconMenu(`sys:${homeView}`, e.currentTarget, () => buildersRef.current.systemAppMenu(homeView)); return }
          onViewChange(homeView)
        }}
        onContextMenu={(e) => { e.preventDefault(); openIconMenu(`sys:${homeView}`, e.currentTarget, () => buildersRef.current.systemAppMenu(homeView)) }}
        className={`w-10 h-10 mt-1 mb-2 shrink-0 flex items-center justify-center rounded-lg transition-colors ${
          activeView === homeView ? 'ws-rail-active' : 'hover:themed-bg-tertiary'
        }`}
        data-tooltip={productName}
        data-tooltip-placement="right"
        aria-label={productName}
        data-testid="taskbar-home"
      >
        {/* decorative: the button already carries aria-label={productName}, so
            an announced logo would name the brand twice. */}
        <BrandLogo decorative className="w-5.5 h-5.5 pointer-events-none" />
      </button>
      {items.map((item) => (
        <button
          key={item.id}
          ref={item.id === 'workspace' ? workspaceButtonRef : undefined}
          data-menu-owner={`sys:${item.id}`}
          onPointerDown={(e) => startHold(`sys:${item.id}`, e, () => buildersRef.current.systemAppMenu(item.id))}
          onPointerMove={moveHold}
          onPointerUp={cancelHold}
          onPointerCancel={cancelHold}
          onPointerLeave={cancelHold}
          onClick={(e) => {
            // Press-and-hold already opened the menu — swallow the click that ends the hold.
            if (holdFired.current) { holdFired.current = false; return }
            // This press toggled an open menu shut — don't let the click reopen it.
            if (pressConsumedRef.current) { pressConsumedRef.current = false; return }
            // Second click on the already-active icon → context menu.
            if (activeView === item.id) { openIconMenu(`sys:${item.id}`, e.currentTarget, () => buildersRef.current.systemAppMenu(item.id)); return }
            onViewChange(item.id)
            if (item.id === 'workspace') {
              cancelClose()
              cancelOpen()
              setShowWorkspacePopup(false)
            }
          }}
          onContextMenu={(e) => { e.preventDefault(); openIconMenu(`sys:${item.id}`, e.currentTarget, () => buildersRef.current.systemAppMenu(item.id)) }}
          onMouseEnter={item.id === 'workspace' && workspacePopup
            ? () => { cancelClose(); if (!dragRef.current?.dragging) scheduleOpen(showWorkspacePopup) }
            : showWorkspacePopup ? () => { cancelClose(); cancelOpen(); setShowWorkspacePopup(false) } : cancelOpen}
          onMouseLeave={item.id === 'workspace' ? scheduleClose : undefined}
          onDragEnter={item.id === 'drive' ? handleDriveDragEnter : item.id === 'workspace' ? handleWorkspaceDragEnter : undefined}
          onDragOver={item.id === 'drive' ? handleDriveDragOver : item.id === 'workspace' ? handleWorkspaceDragOver : undefined}
          onDragLeave={item.id === 'drive' ? handleDriveDragLeave : item.id === 'workspace' ? handleWorkspaceDragLeave : undefined}
          onDrop={item.id === 'drive' ? handleDriveIconDrop : item.id === 'workspace' ? handleWorkspaceDrop : undefined}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors relative ${
            activeView === item.id && !(item.id === 'drive' && isDriveDropTarget)
              ? 'ws-rail-active themed-icon-active'
              : item.id === 'drive' && isDriveDropTarget
              ? 'bg-blue-600/30 text-blue-400 ring-2 ring-blue-500'
              : item.id === 'workspace' && isWorkspaceDropTarget
              ? 'bg-green-600/30 text-green-400 ring-2 ring-green-500'
              : activeView === item.id
              ? 'ws-rail-active themed-icon-active'
              : 'themed-icon hover:themed-icon-active hover:themed-bg-tertiary'
          }`}
          // Not while the Active Projects panel is open: that panel IS this icon's hover
          // response, and the hint would land on top of its first card.
          data-tooltip={item.id === 'workspace' && showWorkspacePopup && workspacePopup ? undefined : t(item.labelKey)}
          data-tooltip-placement="right"
          aria-label={t(item.labelKey)}
          // A stable handle for e2e. The label is translated display copy, so a
          // spec pinned to it breaks on any wording change — which is exactly
          // what "Workspace" → "Project Workspace" did.
          data-testid={`taskbar-${item.id}`}
        >
          <item.icon size={20} className="pointer-events-none" />
          <RailIndicator state={activeView === item.id ? 'active' : runningViews?.includes(item.id) ? 'running' : 'idle'} />
        </button>
      ))}
      </div>

      {/* ── The one zone that moves ──────────────────────────────────────
          `min-h-0` is what lets it shrink below its content and therefore
          scroll at all; `flex-1` is what makes it — rather than the tiles —
          absorb the leftover height. `overscroll-contain` keeps a flick at
          the end of the list from scrolling the page behind the rail.
          The scrollbar is hidden because a 56px rail has no room for one;
          the fade at each end is the affordance instead. */}
      {hasRailEntries && (
        <>
          {/* THE SCROLL CHEVRONS. Neither changes the well's size or moves a tile,
              so nothing the sync decides can feed back into what it measures.
              Each pill is painted OUTSIDE the well (its mask would fade a chevron
              at the very edge it marks). Above, the chevron is its own target: it
              stands in for the separator line in the gap under Settings, reaching
              into the list no further than its padding, so it never covers a
              tile. Below, the target is inside the well (see there) and the pill
              is painted over it. Pointer-only helpers, hidden from assistive tech:
              every tile is reachable by keyboard and screen reader directly, and a
              tile that takes keyboard focus is scrolled clear of the fades.
              Pressing one keeps focus where it was. Hidden and click-through
              during a rail or file drag, so the drag reaches the well. */}
          <div className="ws-rail-cue-above relative w-full shrink-0 flex justify-center my-1">
            <div
              ref={moreAboveRef}
              aria-hidden="true"
              data-testid="rail-more-above"
              data-rail-more="above"
              className="ws-rail-more"
              data-label={t('taskBar.scrollAppsUp')}
              {...chevronPress(-1)}
              onMouseEnter={(e) => pillHover(e.currentTarget, true)}
              onMouseLeave={(e) => pillHover(e.currentTarget, false)}
              onWheel={forwardWheel}
            >
              <span data-testid="rail-more-above-pill" className="ws-rail-more-pill"><ChevronUp size={10} strokeWidth={2.75} /></span>
            </div>
            {/* After the chevron, so the chevron can hide it (a sibling selector). */}
            <div className="ws-rail-separator w-8 border-t themed-border-secondary" />
          </div>
        </>
      )}
      {/* The well is ALWAYS rendered, apps or none: it takes the leftover height (so the
          account controls stay at the bottom) and it is the one place a drive item is
          dropped to pin — the FIRST app included. Empty, it shows nothing. */}
      <div
        ref={wellRef}
        data-testid="rail-well"
        data-rail-drop="well"
        // The fade's length and its cap come from rail/geometry.ts, where
        // they are checked against the widest empty run between two glyphs.
        style={{
          '--rail-fade-len': `${RAIL_FADE_PX}px`,
          '--rail-fade-cap': `${RAIL_FADE_MAX_FRACTION * 100}%`,
        } as React.CSSProperties}
        className="ws-rail-well relative flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center gap-2 py-0.5"
        // The folder popup is anchored to a tile's viewport rect; once the
        // well scrolls that anchor is wrong, so the popup closes.
        onScroll={() => { if (openFolder) closeFolder(); drag.reresolve() }}
        onFocus={revealFocusedTile}
        onDragOver={handleFileDragOver}
        onDragLeave={() => setFileGap(null)}
        onDrop={handleFileDrop}
      >
      {entries.map((entry, i) => (entry.type === 'folder' ? renderFolderTile(entry, i) : renderAppTile(entry, i)))}
      {/* Where the drop lands: a translucent copy of the dragged item in the slot
          the tiles have opened. An external file drag (from the drive or the
          home grid) shows a generic app glyph the same way. */}
      {(railGap ?? fileGap) !== null && (
        <div aria-hidden className="ws-rail-landing" style={{ left: 'calc(50% - 20px)', width: RAIL_TILE, height: RAIL_TILE, top: RAIL_PAD + (railGap ?? fileGap ?? 0) * RAIL_SLOT }}>
          {railGap !== null ? landingGhost : <PDAppIcon size={20} />}
        </div>
      )}
      {/* The down chevron's TARGET: inside the well, pinned to its bottom, so a
          touch pan, a wheel or a drag starting on it is the well's own, and a
          click on it can never reach the tile beneath (a finger's tap: the
          rail's touch handlers). Transparent — the mask would fade anything
          drawn here; its pill is painted just below. */}
      <div
        ref={moreBelowRef}
        aria-hidden="true"
        data-testid="rail-more-below"
        data-rail-more="below"
        className="ws-rail-more-hit"
        data-label={t('taskBar.scrollAppsDown')}
        {...chevronPress(1)}
        onMouseEnter={() => pillHover(moreBelowPaintRef.current, true)}
        onMouseLeave={() => pillHover(moreBelowPaintRef.current, false)}
      />
      </div>
      {hasRailEntries && (
          <div className="ws-rail-cue-below relative w-full shrink-0 h-0">
            <span ref={moreBelowPaintRef} aria-hidden="true" data-testid="rail-more-below-pill" className="ws-rail-more">
              <span className="ws-rail-more-pill"><ChevronDown size={10} strokeWidth={2.75} /></span>
            </span>
          </div>
      )}

      {/* ── Fixed bottom ────────────────────────────────────────────────
          `shrink-0` is not decoration: these are what got pushed off screen
          before, and they are the reason the well above is bounded. */}
      <div className="flex flex-col items-center gap-1 shrink-0 pt-1">

      {user && tenants && activeTenantId && onSwitchTenant && onCreateOrg && onSignOut && (
        <AvatarMenu
          user={user}
          tenants={tenants}
          activeTenantId={activeTenantId}
          activeOrgEmail={activeOrgEmail}
          pendingInvites={pendingInvites}
          onSwitchTenant={onSwitchTenant}
          onCreateOrg={onCreateOrg}
          canCreateOrg={canCreateOrg}
          personalIsIdentityOnly={personalIsIdentityOnly}
          onAcceptInvite={onAcceptInvite}
          onDeclineInvite={onDeclineInvite}
          onBlockInvite={onBlockInvite}
          onSignOut={onSignOut}
        />
      )}

      <NotificationBell
        onSubscribe={onNotificationSubscribe}
        onAction={onNotificationAction}
      />
      </div>

      {/* Workspace hover panel */}
      {showWorkspacePopup && workspacePopup && (
        <WorkspaceHoverPanel
          buttonRef={workspaceButtonRef}
          taskBarRef={taskBarRef}
          onMouseEnter={enterPanel}
          onMouseLeave={leavePanel}
        >
          {typeof workspacePopup === 'function'
            ? workspacePopup(() => setShowWorkspacePopup(false))
            : workspacePopup}
        </WorkspaceHoverPanel>
      )}

      {/* The folder island (portaled to <body>, beside the rail). Stays mounted
          for its outro after the folder closes — `closingFolder` is the last
          entry. Keyed by folder id: re-opening must REMOUNT it, or the intro —
          which runs on mount — never plays and the panel teleports. */}
      {popupFolder && popupAnchor && (
        <FolderPopup
          key={popupFolder.id}
          folder={popupFolder}
          tileRect={popupAnchor}
          landingGhost={landingGhost}
          armed={armedKey === railKeyOfFolder(popupFolder)}
          tileFaceRef={(el) => { islandTileRef.current = el }}
          closing={!openFolderEntry}
          onExited={onIslandExited}
          renaming={openFolder?.renaming ?? false}
          // Leave rename mode only if the island is STILL open on this folder.
          // A blur commits (that is how a rename is saved without pressing
          // Enter), and dismissing the island IS a blur — so writing an open
          // state unconditionally re-opened the panel the user had just closed,
          // on top of the app they had clicked. Same for cancel.
          onStartRename={() => setOpenFolder(cur => (cur && cur.id === popupFolder.id ? { id: cur.id, renaming: true } : cur))}
          onRename={(title) => {
            onUpdateFolder?.(popupFolder.id, { title })
            setOpenFolder(cur => (cur && cur.id === popupFolder.id ? { id: cur.id, renaming: false } : cur))
          }}
          onRenameCancel={() => setOpenFolder(cur => (cur && cur.id === popupFolder.id ? { id: cur.id, renaming: false } : cur))}
          onOpenApp={(app) => { if (drag.consumeClick()) return false; closeFolder(); onOpenUserApp?.(app.ref); return true }}
          onMemberPointerDown={(app, e) => drag.onPointerDown(e, { type: 'app', kind: app.kind, ref: app.ref, fromFolderId: popupFolder.id }, () => (
            app.kind === 'miniapp'
              ? <MiniappIcon spec={app.icon ?? null} name={app.title || t('taskBar.app')} size={RAIL_APP_GLYPH} refId={app.ref} rev={app.iconRev ?? null} />
              : <Globe size={RAIL_HTML_GLYPH} />
          ))}
          onAppContextMenu={(app, el) => openIconMenu(`app:${app.kind}:${app.ref}`, el, () => buildersRef.current.userAppMenu(app))}
          onClose={() => closeFolder()}
          dragSourceKey={dragSourceKey}
          dragSettling={drag.settling}
          gapIndex={drag.gap?.scope === popupFolder.id ? drag.gap.index : null}
        />
      )}

      {/* The dragged tile's ghost (portaled to <body>) */}
      {drag.ghost}
      <div aria-live="polite" className="sr-only">{announcement}</div>

      {/* Glass icon context menu (portaled to <body>) */}
      {iconMenu.menu && (
        <FileContextMenu
          x={iconMenu.menu.x}
          y={iconMenu.menu.y}
          items={iconMenu.menu.build()}
          menuRef={iconMenu.menuRef}
        />
      )}
    </div>
  )
}

function WorkspaceHoverPanel({
  buttonRef,
  taskBarRef,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  buttonRef: React.RefObject<HTMLButtonElement | null>
  taskBarRef: React.RefObject<HTMLDivElement | null>
  onMouseEnter: () => void
  onMouseLeave: () => void
  children: ReactNode
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const buttonRect = buttonRef.current?.getBoundingClientRect()
  const barRect = taskBarRef.current?.getBoundingClientRect()
  // Anchor the panel right below the LOGO, not at the workspace icon — the
  // icon sits a few rows down the rail, which left a dead band above the
  // panel. Measured off the real logo button so rail spacing changes track;
  // falls back to the workspace icon if the logo ever loses its testid.
  const logoRect = taskBarRef.current?.querySelector('[data-testid="taskbar-home"]')?.getBoundingClientRect()
  const style = buttonRect && barRect ? {
    position: 'fixed' as const,
    top: logoRect ? logoRect.bottom : buttonRect.top,
    left: barRect.right,
    zIndex: 50,
  } : {}

  // The fog targets the app SHELL, not <body>, and that is load-bearing. The
  // shell (`h-screen … ws-stage relative isolate`) sets `isolation: isolate` —
  // a stacking context — so `.ws-taskbar`'s z-index:2 is scoped inside it while
  // the shell itself sits at root `z-index: auto`. A fog at BODY level
  // therefore paints over the whole app however low its z-index, blurring the
  // rail you are pointing at. Rendered INSIDE the shell it competes with the
  // rail directly: z-1 frosts the content, the rail's z-2 stays crisp.
  // `closest()` keeps this a DOM fact rather than a new prop threaded through
  // every TaskBar caller; body is a safe fallback (fogs everything) if the
  // shell class ever moves.
  const shellEl = taskBarRef.current?.closest('.ws-stage') ?? document.body
  return (
    <>
      {/* Full-app fog — the same glass kit the mobile sheets use (the `mobile-`
          prefix is historical; §10.5's fog is shared, and this is its first
          desktop surface).

          pointer-events-none: this is a HOVER panel, not a tap-to-dismiss
          sheet. Swallowing clicks would leave the app dead for the 150ms
          close-grace after the cursor leaves. Purely visual — moving away
          still closes it via the panel's own onMouseLeave.

          ws-clear is LOAD-BEARING (same as MobileNavOverlay): the theme class
          paints an opaque --bg-primary, leaving the fog nothing to frost. */}
      {createPortal(
        <div className={`${themeClass} ws-clear absolute inset-0 z-[1] pointer-events-none`} aria-hidden>
          <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" />
        </div>,
        shellEl,
      )}
      {createPortal(
        // ws-clear again: the theme class is here only to resolve the card CSS
        // vars outside any ThemedArea — without ws-clear it also paints an
        // opaque --bg-primary, which reads as a dark slab behind the cards
        // instead of letting them float on the fog.
        //
        // z-40 sits deliberately BELOW FileContextMenu's z-50: right-clicking a
        // rail icon must put its menu ON TOP of this panel. The panel is
        // transparent, so losing that order doesn't hide the menu — it collides
        // the two sets of text. (Only an issue since the panel moved to a body
        // portal; while it lived inside the rail's stacking context the
        // body-level menu won for free.) Above the fog regardless: the fog
        // renders inside the shell, which sits at root z-auto.
        <div
          className={`${themeClass} ws-clear ws-panel-grow`}
          style={{ ...style, zIndex: 40 }}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onDragOver={onMouseEnter}
          // dragleave BUBBLES from every child, and fires on each boundary the
          // pointer crosses — so reordering cards inside the panel used to
          // schedule a close on every card you passed. `dragover` kept undoing
          // it mid-drag, but on DROP the dragover stops and the last scheduled
          // close survives: the panel vanished ~150ms after a successful move.
          // Only schedule when the drag has really left the panel.
          onDragLeave={(e) => {
            if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
            onMouseLeave()
          }}
          // A drop landing in here is proof the user is still working in the
          // panel — cancel anything already pending (e.g. a close scheduled
          // before the guard above could see the pointer come back).
          onDrop={onMouseEnter}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  )
}
